import React, { useEffect, useState } from 'react'
import PreviousConversations from '../PreviousConversations'
import { usePreviousConversationsStore } from '@/stores/previousConversation/previousConversationStore'
import { useConversationStore } from '@/hooks/useConversationStore'
import { DateTime } from 'luxon'

const BubblePreviousConversations = ({
  kb_id,
  isPreviewMode = false,
  previous_conversation_enabled,
  onConversationClick,
  onNavigate,
  config,
  interfaceType = 'bubble',
  showBackButton = false
}) => {
  const { conversations, fetchConversations } = usePreviousConversationsStore()
  const [conversationsFromKbId, setConversationsFromKbId] = useState([])

  // Use the conversation store with automatic namespacing
  const {
    setCurrentConversation,
    resetCurrentConversation
  } = useConversationStore(kb_id, interfaceType);

  useEffect(() => {
    fetchConversations()
  }, [])

  useEffect(() => {
    if (conversations[kb_id] && !isPreviewMode) {
      // Filter conversations by interfaceType
      const filteredConversations = conversations[kb_id]
        .filter(conv => conv.interfaceType === interfaceType)
        .sort((a, b) => b.createdAt - a.createdAt);

      // If no existing conversations, add a default one
      if (filteredConversations.length === 0) {
        setConversationsFromKbId([
          {
            id: `default-${kb_id}`,
            lastMessage: 'How can I help you today?',
            createdAt: DateTime.now().minus({ days: 1 }).toMillis(),
            isDefault: true
          }
        ]);
      } else {
        setConversationsFromKbId(filteredConversations);
      }
    } else if (isPreviewMode) {
      setConversationsFromKbId([
        {
          lastMessage: 'How to create an AI agent?',
          createdAt: DateTime.now().minus({ days: 2 }).toMillis(),
        },
        {
          lastMessage: 'How do I white-label my AI Chatbot?',
          createdAt: DateTime.now().minus({ days: 15 }).toMillis(),
        },
      ])
    } else if (!conversations[kb_id] && !isPreviewMode) {
      // If no conversations exist for this kb_id at all, add a default one
      setConversationsFromKbId([
        {
          id: `default-${kb_id}`,
          lastMessage: 'How can I help you today?',
          createdAt: DateTime.now().minus({ days: 1 }).toMillis(),
          isDefault: true
        }
      ]);
    }
  }, [conversations, kb_id, isPreviewMode, interfaceType])

  const handleConversationClick = (conversationId, conversation) => {
    if (isPreviewMode) {
      return;
    }

    // Check if this is a default conversation
    if (conversation.isDefault) {
      // For default conversations, start a new conversation instead
      setCurrentConversation({
        id: null,
        type: null
      })

      if (onNavigate) {
        onNavigate('chat')
      }
      return
    }

    // Set the current conversation in the store first
    setCurrentConversation({
      id: conversationId,
      type: 'opening'
    })

    if (onConversationClick) {
      onConversationClick(conversationId, conversation)
    }
  }

  // Transform conversations to match PreviousConversations component format
  const transformedConversations = conversationsFromKbId.map((conversation, index) => ({
    id: conversation.id || index,
    title: conversation.lastMessage || `Conversation - ${conversation.id}`,
    agent: config?.message_disclaimer_text || "Dante AI",
    agentType: "AI Agent",
    timestamp: DateTime.fromMillis(
      conversation.createdAt ?? DateTime.now().toMillis(),
      { zone: 'utc' }
    ).toRelative(),
    avatar: "D",
    isDefault: conversation.isDefault || false
  }))

  // Don't render anything if previous conversations are disabled
  if (!previous_conversation_enabled) {
    return (
      <div 
        className='overflow-y-auto no-scrollbar h-full w-[427px] flex items-center justify-center'
        style={{ backgroundColor: config?.surface_color || '#ffffff' }}
      >
        <div className="text-center p-v3-md">
          <p className="text-v3-black-secondary text-v3-h6">Previous conversations are disabled</p>
        </div>
      </div>
    )
  }

  return (
     <PreviousConversations
        conversations={transformedConversations}
        onConversationClick={(conversation) => handleConversationClick(conversation.id, conversation)}
        onNavigate={onNavigate}
        config={config}
        resetCurrentConversation={resetCurrentConversation}
        setCurrentConversation={setCurrentConversation}
        showBackButton={showBackButton}
      />
  )
}

export default BubblePreviousConversations